import React, { useEffect, useRef, useState } from "react";
import ChatFriendList from "../Components/ChatFriendList";
import "../css/chat.css";
import Layout from "../Layout/Layout";

import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { api } from "../actions/api";
import ChatMessageBox from "../Components/Chat/ChatMessageBox";
import FilePreviewModal from "../Components/Chat/FilePreviewModal";
import ReceiveChat from "../Components/Chat/ReceiveChat";
import SendChat from "../Components/Chat/SendChat";
import { FIREBASE_CONVERSATIONS } from "../Utils/constant";
import { db } from "../Utils/firebase";
import AddNewMessage from "./AddNewMessage";
import { set } from "react-hook-form";
import { generateThumbnail } from "../helpers/GenerateThumbnailHelper";
import convertToMp3 from "../Utils/convertToMp3";
import { formatTime } from "../Utils/helpers";
import { useSearchParams } from "react-router-dom";
import useUserStore from "../zustand-stores/UserStore";
import { sendFcmNotificationToTopic } from "../helpers/NotificationHelper";
import {
  buildUnreadUserIds,
  clearSenderUnread,
  getUnreadCountForUser,
  isConversationUnreadForUser,
  isLastMessageFromUser,
  normalizeUserId,
} from "../Utils/chatUnread";
import useDirectChatOnlineStatus from "../hooks/useDirectChatOnlineStatus";
import GroupAboutSidebar from "../Components/GroupAboutSidebar";
import useChatAutoScroll from "../hooks/useChatAutoScroll";
import { updateUserOnlineStatus } from "../hooks/useFireStore";

const CONVERSATION_PAGE_SIZE = 10;
const MESSAGE_PAGE_SIZE = 20;

const getAttachmentPreviewText = (type) => {
  if (type === "audio") return "[Audio Message]";
  return type;
};

export default function Chat() {
  const [searchParams] = useSearchParams();
  const conversationIdFromUrl = searchParams.get("conversationId");

  const [userProfile, setUserProfile] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentChatType, setCurrentChatType] = useState(null);
  const [messages, setMessages] = useState([]);
  const currentTypeRef = useRef(null);
  const [messageReactions, setMessageReactions] = useState({});
  const [conversationId, setConversationId] = useState(null);
  const [deleteConversationId, setDeleteConversationId] = useState(null);
  const [showAddMessage, setShowAddMessage] = useState(false);
  const [isMember, setIsMember] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [lastMessageDoc, setLastMessageDoc] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasNext, setHasNext] = useState(true);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  // States
  const [chatPartners, setChatPartners] = useState([]);
  const [currentChatUser, setCurrentChatUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [likedMessages, setLikedMessages] = useState([]);
  const [isClientReady, setIsClientReady] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [clickedUserInfo, setClickedUserInfo] = useState({
    name: "",
    profileImage: "",
    members: 0,
  });
  const [lastDoc, setLastDoc] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [unsubscribe, setUnsubscribe] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [disabledEditorButton, setDisabledEditorButton] = useState(false);
  const [showUnread, setShowUnread] = useState(false);
  // Refs
  const loadMoreRef = useRef(null);
  const topMessageRef = useRef(null);
  const lastMessageDocRef = useRef(null);
  const topSentinelRef = useRef(null);
  // Optimistic "uploading" messages shown while a file is being uploaded, before
  // the real Firestore document exists. Kept in a ref so the messages snapshot
  // listener can merge them in without them being wiped mid-upload.
  const pendingUploadsRef = useRef([]);

  const setPendingUploadsState = (updater) => {
    pendingUploadsRef.current =
      typeof updater === "function"
        ? updater(pendingUploadsRef.current)
        : updater;
  };

  const { user } = useUserStore();

  const handleFileClick = (file) => {
    setPreviewFile(file);
  };

  const handleDelete = (conversationId) => {
    setDeleteConversationId(conversationId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    handleDeleteConversation(deleteConversationId);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  // Check membership status listener
  useEffect(() => {
    if (!conversationId || !userProfile?.id) return;

    const groupRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);

    const unsubscribe = onSnapshot(groupRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data?.type === "group") {
          const isParticipant = data.participants?.includes(
            String(userProfile.id),
          );
          setIsMember(isParticipant);
        } else {
          setIsMember(true);
        }
      } else {
        // If doc doesn't exist, assume removed or deleted.
        // However, if it's just loading or error, handle carefully.
        // For now, if doc missing, it's likely deleted/removed.
        setIsMember(false);
      }
    });

    return () => unsubscribe();
  }, [conversationId, userProfile?.id]);

  // Handlers (Placeholder functions)
  const handleDownload = async (file) => {
    if (!file?.url) {
      return;
    }

    try {
      const response = await fetch(file.url, {
        cache: "no-store", // ensures request bypasses cache
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const fileExtension = file.url.split(".").pop().split("?")[0]; // e.g., jpg, png
      const fileName = `downloaded-file.${fileExtension}`;

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(link.href), 150);
    } catch (err) {
      console.error("File download error:", err);
    }
  };

  const handleReply = (message) => {
    setReplyingTo({
      id: message.conversationId + "_" + message.id,
      count: 1,
      messageId: message.id,
      parentMessage: {
        content: message.content,
        userImage: message.userImage,
        owner: userProfile?.full_name,
      },
    });
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleLike = async (messageId, emoji) => {
    const userId = userProfile.id;
    const messageRef = doc(
      db,
      FIREBASE_CONVERSATIONS,
      conversationId,
      "messages",
      messageId,
    );
    try {
      // Get the current message data to check if already liked
      const messageSnap = await getDoc(messageRef);

      if (!messageSnap.exists()) {
        console.error("Message not found");
        return;
      }

      const messageData = messageSnap.data();
      const currentLikes = messageData.reactions?.like || [];

      // Find if user already reacted with a different emoji
      let previousEmoji = null;
      for (const [key, users] of Object.entries(messageData.reactions)) {
        if (users.includes(String(userId))) {
          previousEmoji = key;
          break;
        }
      }

      const updates = {};

      // If clicking the same emoji again → remove it
      if (previousEmoji === emoji) {
        const updatedUsers = messageData.reactions[emoji].filter(
          (id) => id != userId,
        );

        if (updatedUsers.length === 0) {
          updates[`reactions.${emoji}`] = deleteField();
        } else {
          updates[`reactions.${emoji}`] = updatedUsers;
        }

        await updateDoc(messageRef, updates);
        return;
      }

      // If reacted with a different emoji → remove old one
      if (previousEmoji) {
        const updatedUsers = messageData.reactions[previousEmoji].filter(
          (id) => id != userId,
        );

        if (updatedUsers.length === 0) {
          updates[`reactions.${previousEmoji}`] = deleteField();
        } else {
          updates[`reactions.${previousEmoji}`] = updatedUsers;
        }
      }

      // Add user to the new emoji array
      updates[`reactions.${emoji}`] = arrayUnion(String(userId));

      await updateDoc(messageRef, updates);

      // // Add user to the new emoji array
      // await updateDoc(messageRef, {
      //   [`reactions.${emoji}`]: arrayUnion(String(userId)),
      // });

      // const isLiked = currentLikes.includes(String(userId));

      // if (isLiked) {
      //   // If already liked, remove the like
      //   await updateDoc(messageRef, {
      //     "reactions.like": arrayRemove(String(userId)),
      //   });
      // } else {
      //   // Otherwise, add the like
      //   await updateDoc(messageRef, {
      //     "reactions.like": arrayUnion(String(userId)),
      //   });
      // }
    } catch (error) {
      console.error("Error toggling reactions:", error);
    }
  };

  const handleEdit = (messageId, oldText) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? { ...msg, isEditing: true, content: oldText }
          : msg,
      ),
    );
  };

  const handleSaveEdit = async (
    messageId,
    newContent,
    file,
    mentionedIds = [],
  ) => {
    try {
      const messageRef = doc(
        db,
        FIREBASE_CONVERSATIONS,
        conversationId,
        "messages",
        messageId,
      );
      await updateDoc(messageRef, {
        content: newContent,
        mentionedIds: mentionedIds || [],
      });

      // Update lastMessageMentionedIds on conversation if it's the last message
      const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
      const convoSnap = await getDoc(convoRef);
      if (convoSnap.exists()) {
        const convoData = convoSnap.data();
        if (
          convoData.lastMessage ===
          messages.find((m) => m.id === messageId)?.content
        ) {
          await updateDoc(convoRef, {
            lastMessage: newContent,
            lastMessageMentionedIds: mentionedIds || [],
          });
        }
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, isEditing: false } : msg,
        ),
      );
    } catch (error) {
      console.error("Failed to save edited message:", error);
      // Optionally, you can revert the state change if saving fails
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, isEditing: true } : msg,
        ),
      );
    }
  };

  const handleCancelEdit = (messageId) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ? { ...msg, isEditing: false } : msg,
      ),
    );
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const messageRef = doc(
        db,
        FIREBASE_CONVERSATIONS,
        conversationId,
        "messages",
        messageId,
      );

      await updateDoc(messageRef, {
        visibleTo: arrayRemove(String(userProfile?.id)),
      });

      const messageSnap = await getDoc(messageRef);
      if (messageSnap.exists()) {
        const data = messageSnap.data();
        if (!data.visibleTo || data.visibleTo.length === 0) {
          await deleteDoc(messageRef);
        }
      }

      // 2. Update local state
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    } catch (error) {
      console.error("Failed to delete message:", error);
    }
  };

  const handleSendMessage = async (content, file, mentionedIds = []) => {
    setAutoScrollEnabled(true);

    setLoading(true);

    let fileData = null;
    let message = null;
    let newMessageId = null;

    // Show an optimistic "uploading" bubble immediately so the attachment isn't
    // rendered as an empty message while it uploads. Replies live in a separate
    // thread subcollection (not this list), so only do it for main-list sends.
    let tempUploadId = null;
    const clearOptimisticUpload = () => {
      if (!tempUploadId) return;
      const id = tempUploadId;
      tempUploadId = null;
      setPendingUploadsState((prev) =>
        prev.filter((m) => m.tempUploadId !== id),
      );
      setMessages((prev) => prev.filter((m) => m.tempUploadId !== id));
    };

    if (file && !replyingTo) {
      tempUploadId = `temp-upload-${Date.now()}`;
      const localFileType = file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
          ? "video"
          : file.type.startsWith("audio/")
            ? "audio"
            : "document";
      const optimisticMessage = {
        id: tempUploadId,
        tempUploadId,
        senderId: String(userProfile.id),
        senderName: userProfile.full_name,
        senderProfileImage: userProfile.image,
        content: content || "",
        isFile: true,
        isUploading: true,
        uploadProgress: 0,
        file: { url: null, type: localFileType, name: file.name },
        reactions: {},
        replyTo: null,
        timestamp: { seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 },
      };
      setPendingUploadsState((prev) => [optimisticMessage, ...prev]);
      setMessages((prev) => [optimisticMessage, ...prev]);
    }

    const updateUploadProgress = (pct) => {
      const id = tempUploadId;
      if (!id) return;
      const apply = (m) =>
        m.tempUploadId === id ? { ...m, uploadProgress: pct } : m;
      setPendingUploadsState((prev) => prev.map(apply));
      setMessages((prev) => prev.map(apply));
    };

    if (file) {
      fileData = await handleSendFile(file, updateUploadProgress);
      if (!content) {
        message = getAttachmentPreviewText(fileData?.type);
      }
    }

    try {
      const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
      const convoSnap = await getDoc(convoRef);

      if (!convoSnap.exists()) {
        console.error("Conversation not found");
        return;
      }
      const convoData = convoSnap.data();
      const visibleToUserIds = convoData.participants || [];

      if (replyingTo) {
        setDisabledEditorButton(true);
        // This is a reply - store it in threads subcollection
        const threadMessageData = {
          type: "text",
          metadata: {},
          reactions: {},
          isRead: null,
          senderId: userProfile.id,
          senderName: userProfile.full_name,
          senderProfileImage: userProfile.image,
          timestamp: serverTimestamp(),
          content: content,
          isFile: !!fileData,
          visibleTo: visibleToUserIds,
          mentionedIds: mentionedIds || [],
        };

        if (fileData) {
          threadMessageData.file = {
            url: fileData.media_url || null,
            type: fileData.type || null,
          };
        }

        const threadRef = doc(db, "threads", replyingTo.id);
        const threadSnap = await getDoc(threadRef);

        let threadExists = true;

        // Update the original message to increment reply count and set first message
        const originalMessageRef = doc(
          db,
          FIREBASE_CONVERSATIONS,
          conversationId,
          "messages",
          replyingTo.messageId,
        );

        await runTransaction(db, async (transaction) => {
          const originalMsgSnap = await transaction.get(originalMessageRef);

          if (originalMsgSnap.exists()) {
            const originalMsgData = originalMsgSnap.data();

            if (!threadSnap.exists()) {
              threadExists = false;
              await setDoc(threadRef, {
                createdAt: serverTimestamp(),
                id: replyingTo.id,
                visibleTo: visibleToUserIds,
                mainMessage: originalMsgData,
                conversationId: conversationId,
              });
            }

            if (!threadExists) {
              await addDoc(
                collection(db, "threads", replyingTo.id, "messages"),
                originalMsgData,
              );
            }

            const currentReplyTo = originalMsgData.replyTo || { count: 0 };

            transaction.update(originalMessageRef, {
              replyTo: {
                id: `${replyingTo.id}`,
                count: (currentReplyTo.count || 0) + 1,
                message: content,
              },
            });
          }
        });

        // Add the reply to threads
        const newMessageRef = await addDoc(
          collection(db, "threads", replyingTo.id, "messages"),
          threadMessageData,
        );
        newMessageId = newMessageRef.id;
      } else {
        const messageData = {
          type: "text",
          metadata: {},
          reactions: {},
          isRead: null,
          senderId: userProfile.id,
          senderName: userProfile.full_name,
          senderProfileImage: userProfile.image,
          timestamp: serverTimestamp(),
          content: content,
          isFile: !!fileData, // true or false
          visibleTo: visibleToUserIds,
          mentionedIds: mentionedIds || [],
        };

        if (fileData) {
          messageData.file = {
            thumbnail: fileData.media_url || null,
            url:
              fileData.video_url ||
              fileData.media_url ||
              fileData.audio_url ||
              null,
            type: fileData.type || null,
          };
        }

        const newMessageRef = await addDoc(
          collection(db, FIREBASE_CONVERSATIONS, conversationId, "messages"),
          messageData,
        );
        newMessageId = newMessageRef.id;
        // The real message now exists and will stream in via the snapshot
        // listener, so drop the optimistic bubble to avoid a duplicate.
        clearOptimisticUpload();
      }

      await runTransaction(db, async (transaction) => {
        const convoSnap = await transaction.get(convoRef);
        if (!convoSnap.exists()) return;

        const convoData = convoSnap.data();

        const currentUnread = convoData.unreadCount || {};

        // Use only participants (not visibleTo) to avoid re-adding removed members
        const allParticipants = (convoData.participants || []).filter(Boolean);

        // A new message brings the conversation back for participants who
        // removed themselves via "delete for me", so re-add them to visibleTo
        // (removed group members are not in participants and stay out).
        const updatedVisibleTo = Array.from(
          new Set([
            ...(convoData.visibleTo || []).map(String),
            ...allParticipants.map(String),
          ]),
        );

        const updatedUnread = { ...currentUnread };

        // Ensure every participant is in the map and increment if they pass internal sanity check
        allParticipants.forEach((uid) => {
          const uidStr = String(uid);

          const isNotSender = uidStr !== String(userProfile.id);
          const isVisible = updatedVisibleTo.includes(uidStr);
          const isInDetails =
            convoData.type === "group"
              ? convoData.participantDetails &&
                uidStr in convoData.participantDetails
              : true;

          if (isNotSender && isVisible && isInDetails) {
            if (!(uidStr in updatedUnread)) {
              updatedUnread[uidStr] = 0;
            }
            updatedUnread[uidStr] = (updatedUnread[uidStr] || 0) + 1;
          }
        });

        clearSenderUnread(updatedUnread, userProfile.id);

        const unreadUserIds = buildUnreadUserIds(updatedUnread);

        if (content === "") {
          content = message;
        }

        transaction.update(convoRef, {
          lastMessage: content,
          lastMessageSenderId: String(userProfile.id),
          lastMessageTime: serverTimestamp(),
          updatedAt: serverTimestamp(),
          unreadCount: updatedUnread,
          unreadUserIds,
          participants: allParticipants, // Repair the participants list
          visibleTo: updatedVisibleTo,
          lastMessageMentionedIds: mentionedIds || [],
          // Track if last message is from a thread for navigation
          lastMessageThreadId: replyingTo ? replyingTo.id : null,
        });
      });

      const convoSnapAfter = await getDoc(convoRef);
      const convoDataAfter = convoSnapAfter.data();
      // Use only participants from the updated snap
      const allParticipants = (convoDataAfter.participants || []).filter(
        Boolean,
      );

      // Send Push Notification
      const isGroup = currentChatType === "group";
      const notificationTitle = isGroup
        ? `${userProfile.full_name} in ${convoDataAfter.metadata?.groupName || "Group"}`
        : `${userProfile.full_name} sent you a message`;

      sendFcmNotificationToTopic({
        title: notificationTitle,
        body: content || (fileData ? "Attachment" : ""),
        conversationId: conversationId,
        threadId: replyingTo ? replyingTo.id : null,
        isGroupChat: isGroup,
        recipientIds: allParticipants,
        senderId: userProfile.id,
        messageId: newMessageId || `${conversationId}_${Date.now()}`,
      });

      if (replyingTo) {
        setReplyingTo(null);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      // Remove the optimistic uploading bubble: on success the real message has
      // arrived via the snapshot listener; on failure we simply drop it.
      clearOptimisticUpload();
      setLoading(false);
      setDisabledEditorButton(false);
    }
  };

  const chunkSize = 5 * 1024 * 1024; // 5MB per chunk

  function generateRandomString(length) {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  const uploadFileInChunks = async (file, fileId, onProgress) => {
    const totalChunks = Math.ceil(file.size / chunkSize);

    for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber++) {
      // Reserve 0-90% for chunk uploads; the thumbnail post covers the rest.
      if (typeof onProgress === "function") {
        onProgress(Math.round(((chunkNumber - 1) / totalChunks) * 90));
      }
      const start = (chunkNumber - 1) * chunkSize;
      const end = Math.min(file.size, start + chunkSize);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("file_id", fileId);
      formData.append("chunk_number", chunkNumber);
      formData.append("total_chunks", totalChunks);
      formData.append("file_name", file.name);
      formData.append("chunk", chunk);
      await api.post("/upload-chunk", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
  };

  const handleSendFile = async (file, onProgress) => {
    setLoading(true);
    setDisabledEditorButton(true);
    try {
      // Ensure audio files are converted to MP3 before upload
      if (
        file &&
        file.type &&
        file.type.startsWith("audio") &&
        file.type !== "audio/mpeg"
      ) {
        try {
          const mp3File = await convertToMp3(file);
          if (mp3File) file = mp3File;
        } catch (err) {
          console.warn(
            "Failed to convert to MP3, uploading original audio:",
            err,
          );
        }
      }
      const formData = new FormData();

      const uploadedVideoIds = [];

      try {
        if (file.type.startsWith("video")) {
          const fileId = generateRandomString(10);
          await uploadFileInChunks(file, fileId, onProgress);
          uploadedVideoIds.push(fileId);

          // Generate thumbnail for mobile
          const thumbnail = await generateThumbnail(file);

          if (thumbnail) {
            formData.append("media", thumbnail);
          }
        } else if (file.type.startsWith("audio")) {
          formData.append("audio", file);
        } else {
          formData.append("media", file);
        }

        // 2. Append videoIds once after loop
        if (uploadedVideoIds.length > 0) {
          formData.append("videoIds", JSON.stringify(uploadedVideoIds));
        }
      } catch (e) {
        setLoading(false);
      }

      const response = await api.post("/user/chat/media/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (evt) => {
          if (typeof onProgress === "function" && evt.total) {
            // Videos upload their chunks first, so reserve the final 10% here.
            const raw = Math.round((evt.loaded / evt.total) * 100);
            const pct = file.type.startsWith("video")
              ? 90 + Math.round(raw * 0.1)
              : raw;
            onProgress(Math.min(99, pct));
          }
        },
      });

      if (!response.status == 200) {
        throw new Error("Upload failed");
      }

      const fileData = response.data;

      setLoading(false);
      return fileData;
    } catch (error) {
      console.error("Error uploading file:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // const profile = userPro;
        setUserProfile(user);
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (!conversationIdFromUrl && conversations.length) return;

    const conversation = conversations.find(
      (c) => c.id == conversationIdFromUrl,
    );

    if (!conversation) return;

    if (conversation.type == "group") {
      handleSelectUser(
        conversation.id || conversation.conversationId,
        conversation.type,
        "/icons/group-user.svg",
        conversation.metadata.groupName || "Unknown",
        conversation.participants.length,
      );
    }

    if (conversation.type == "direct") {
      const otherUserId = conversation.participants.find(
        (id) => id != userProfile?.id,
      );

      if (!otherUserId) return;

      // Fetch user details from users collection (with cache check)
      const fetchUser = async () => {
        try {
          // Check cache first
          let otherUser = userDetailsCache[otherUserId];

          if (!otherUser) {
            // Fetch from Firestore if not in cache
            const userRef = doc(db, "users", String(otherUserId));
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              otherUser = { id: userSnap.id, ...userSnap.data() };
              // Update cache
              setUserDetailsCache((prev) => ({
                ...prev,
                [otherUserId]: otherUser,
              }));
            } else {
              console.warn("User not found:", otherUserId);
              return;
            }
          }

          handleSelectUser(
            conversation.id || conversation.conversationId,
            conversation.type,
            otherUser.profileImage || otherUser.image || "",
            otherUser.name || otherUser.full_name || "Unknown",
            conversation.participants.length,
          );
        } catch (err) {
          console.error("Error fetching user:", err);
        }
      };

      fetchUser();
    }
  }, [conversationIdFromUrl, conversations.length, userProfile?.id]);

  const [userDetailsCache, setUserDetailsCache] = useState({});
  const onlineStatusMap = useDirectChatOnlineStatus(
    conversations,
    userProfile?.id,
  );

  const { scrollContainerRef } = useChatAutoScroll({
    enabled: autoScrollEnabled,
    messages,
    conversationId,
  });

  useEffect(() => {
    if (!userProfile?.id) return;

    updateUserOnlineStatus(userProfile, true);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updateUserOnlineStatus(userProfile, true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [userProfile?.id]);

  // Optimized function to batch fetch user details from users collection
  const fetchUserDetailsBatch = async (userIds) => {
    if (!userIds || userIds.length === 0) return {};

    // Filter out already cached users
    const uncachedIds = userIds.filter((id) => !userDetailsCache[id]);
    if (uncachedIds.length === 0) {
      // Return cached data
      return userIds.reduce((acc, id) => {
        if (userDetailsCache[id]) {
          acc[id] = userDetailsCache[id];
        }
        return acc;
      }, {});
    }

    try {
      // Batch fetch users by document ID
      // Note: Firestore allows direct document access by ID, no need for query
      const userPromises = uncachedIds.map(async (userId) => {
        try {
          const userRef = doc(db, "users", String(userId));
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            return {
              id: userId,
              data: { id: userSnap.id, ...userSnap.data() },
            };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          return null;
        }
      });

      const results = await Promise.all(userPromises);
      const newUserDetails = {};

      results.forEach((result) => {
        if (result) {
          newUserDetails[result.id] = result.data;
        }
      });

      // Update cache
      setUserDetailsCache((prev) => ({ ...prev, ...newUserDetails }));

      // Return combined cached and new data
      return {
        ...userDetailsCache,
        ...newUserDetails,
      };
    } catch (error) {
      console.error("Error batch fetching user details:", error);
      return userDetailsCache;
    }
  };

  // Conversations listener
  const userId = userProfile?.id;
  useEffect(() => {
    if (!userId) return;

    // Always use the same collection; switch filters based on toggle.
    // Note: Firestore doesn't allow multiple array-contains in a single query,
    // so we use participants OR unreadUserIds depending on showUnread.
    const baseRef = collection(db, FIREBASE_CONVERSATIONS);
    const q = showUnread
      ? query(
          baseRef,
          where("unreadUserIds", "array-contains", String(userId)),
          orderBy("lastMessageTime", "desc"),
        )
      : query(
          baseRef,
          where("participants", "array-contains", String(userId)),
          orderBy("lastMessageTime", "desc"),
        );

    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const userConversations = [];
      const userIdsToFetch = new Set();

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const userIdStr = String(userProfile.id);
        const isVisible = data.visibleTo?.includes(userIdStr);

        // Safety Backup: If group, also check participantDetails keys
        const isInDetails =
          data.type === "group"
            ? data.participantDetails && userIdStr in data.participantDetails
            : true;

        if (isVisible && isInDetails) {
          userConversations.push({ id: doc.id, ...data });

          // Collect all participant IDs to fetch from users collection
          if (data.participants) {
            data.participants.forEach((id) => {
              if (id !== userIdStr) {
                userIdsToFetch.add(String(id));
              }
            });
          }
        }
      });

      setConversations(userConversations);

      // Batch fetch user details for all participants
      if (userIdsToFetch.size > 0) {
        await fetchUserDetailsBatch(Array.from(userIdsToFetch));
      }
    });

    // Clean up on unmount
    return () => unsubscribe();
  }, [userId, showUnread]);

  // Messages listener
  useEffect(() => {
    if (!conversationId || !userId) return;

    const messagesRef = collection(
      db,
      FIREBASE_CONVERSATIONS,
      conversationId,
      "messages",
    );

    const q = query(
      messagesRef,
      where("visibleTo", "array-contains", String(userId)),
      orderBy("timestamp", "desc"),
      limit(MESSAGE_PAGE_SIZE),
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const latest = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        replyCount: doc.data().replyTo?.count || 0,
      }));

      // Keep any still-uploading optimistic messages on top (newest first) so
      // they survive interim snapshots until their real document arrives.
      const pending = pendingUploadsRef.current;
      setMessages(pending.length ? [...pending, ...latest] : latest);
      lastMessageDocRef.current =
        snapshot.docs[snapshot.docs.length - 1] ?? null;
      setHasNext(snapshot.docs.length === MESSAGE_PAGE_SIZE);

      // Fetch user details for all message senders from users collection
      const senderIds = new Set();
      latest.forEach((msg) => {
        if (msg.senderId && msg.senderId !== String(userId)) {
          senderIds.add(String(msg.senderId));
        }
      });

      if (senderIds.size > 0) {
        await fetchUserDetailsBatch(Array.from(senderIds));
      }
    });

    return () => {
      unsubscribe();
    };
  }, [conversationId, userId]);

  // Fetch user details for message senders when messages change
  useEffect(() => {
    if (!messages.length) return;

    const senderIds = new Set();
    messages.forEach((msg) => {
      if (msg.senderId && msg.senderId !== String(userProfile?.id)) {
        senderIds.add(String(msg.senderId));
      }
    });

    if (senderIds.size > 0) {
      fetchUserDetailsBatch(Array.from(senderIds));
    }
  }, [messages]);

  // Load older messages (manual, not real-time)
  const loadOlderMessages = async () => {
    if (!lastMessageDocRef.current || pageLoading || !hasNext) return;
    setPageLoading(true);

    const messagesRef = collection(
      db,
      FIREBASE_CONVERSATIONS,
      conversationId,
      "messages",
    );
    const q = query(
      messagesRef,
      where("visibleTo", "array-contains", String(userProfile.id)),
      orderBy("timestamp", "desc"),
      startAfter(lastMessageDocRef.current),
      limit(MESSAGE_PAGE_SIZE),
    );

    try {
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const older = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages((prev) => [...prev, ...older]); // prepend older messages

        lastMessageDocRef.current = snapshot.docs[snapshot.docs.length - 1];
        setHasNext(snapshot.docs.length === MESSAGE_PAGE_SIZE);
      } else {
        setHasNext(false);
      }
    } catch (err) {
      console.error("Error loading older messages:", err);
    } finally {
      setPageLoading(false);
    }
  };

  const resetUnreadCountInDB = async (conversationId) => {
    const uid = userProfile?.id != null ? String(userProfile.id) : "";
    if (!uid || !conversationId) return;

    const conversationRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);

    try {
      // Clear every piece of notification state for THIS user unconditionally.
      // Each field is scoped to the current user (a single map key / array
      // member), so we can write directly without a read-modify-write
      // transaction — that guarantees the badge, the unread filter and the
      // header mention dot all clear as soon as the chat is opened, and avoids
      // clobbering other participants' counts.
      await updateDoc(conversationRef, {
        // unread count badge + isUnread for this user
        [`unreadCount.${uid}`]: 0,
        // drives the "Unread Chats" filter query (unreadUserIds array-contains)
        unreadUserIds: arrayRemove(uid),
        // header notification mail-dot (mentioned-in-last-message)
        lastMessageMentionedIds: arrayRemove(uid),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error resetting unread/mention:", error);
    }
  };

  // While a chat is open, new incoming messages still increment unread in Firestore.
  // Reset as soon as we detect unread on the active conversation.
  useEffect(() => {
    if (!conversationId || !userProfile?.id) return;

    const activeConvo = conversations.find(
      (c) => (c.id || c.conversationId) === conversationId,
    );
    if (!activeConvo) return;

    const uid = String(userProfile.id);
    const unread = getUnreadCountForUser(
      activeConvo.unreadCount,
      userProfile.id,
    );
    // The header notification also lights up on mentions, independent of the
    // unread count. Clear when either is set so an @mention that arrives while
    // the chat is open (and doesn't bump our unread count) still gets reset.
    const isMentioned = Array.isArray(activeConvo.lastMessageMentionedIds)
      ? activeConvo.lastMessageMentionedIds.map(String).includes(uid)
      : false;

    if (unread > 0 || isMentioned) {
      resetUnreadCountInDB(conversationId);
    }
  }, [conversationId, conversations, userProfile?.id]);

  const handleSelectUser = async (
    conversationId,
    type,
    profileImage,
    name,
    members,
  ) => {
    setCurrentChatType(type);
    setConversationId(conversationId);
    setClickedUserInfo({
      profileImage: profileImage,
      name: name || "Squad",
      members: members || 0,
    });
    setShowAddMessage(false);
    setAutoScrollEnabled(true);

    await resetUnreadCountInDB(conversationId);
  };

  const showAddNewMessage = () => {
    setShowAddMessage(true);
  };

  const handleAddNewMessageClose = () => {
    setShowAddMessage(false);
    setConversationId(null); // Reset conversationId here
  };

  const handleDeleteConversation = async (conversationId) => {
    const uid = String(userProfile?.id);
    const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
    const messagesRef = collection(
      db,
      FIREBASE_CONVERSATIONS,
      conversationId,
      "messages",
    );

    try {
      await updateDoc(convoRef, {
        visibleTo: arrayRemove(uid),
        deletedBy: arrayUnion(uid),
      });

      // "Delete for me": also remove this user from every message's visibleTo.
      // Without this the history is only hidden, and it all reappears when the
      // conversation becomes visible again (e.g. the other participant sends a
      // new message). Messages no one can see anymore are deleted outright.
      // Firestore batches cap at 500 ops, so commit in chunks.
      const messagesSnap = await getDocs(messagesRef);
      const BATCH_LIMIT = 500;
      let batch = writeBatch(db);
      let opCount = 0;
      const commits = [];

      const flushBatch = () => {
        commits.push(batch.commit());
        batch = writeBatch(db);
        opCount = 0;
      };

      messagesSnap.forEach((msgDoc) => {
        const visibleTo = (msgDoc.data().visibleTo || []).map(String);
        if (!visibleTo.includes(uid)) return;

        const remaining = visibleTo.filter((id) => id !== uid);
        if (remaining.length === 0) {
          batch.delete(msgDoc.ref);
        } else {
          batch.update(msgDoc.ref, { visibleTo: remaining });
        }

        opCount++;
        if (opCount === BATCH_LIMIT) flushBatch();
      });

      if (opCount > 0) flushBatch();
      await Promise.all(commits);

      // If every participant has deleted the conversation, remove it entirely,
      // including any message docs left over (e.g. only visible to users who
      // were removed from the group).
      const convoSnap = await getDoc(convoRef);
      if (convoSnap.exists()) {
        const data = convoSnap.data();
        if (!data.visibleTo || data.visibleTo.length === 0) {
          const leftoverSnap = await getDocs(messagesRef);
          let deleteBatch = writeBatch(db);
          let deleteCount = 0;
          const deleteCommits = [];

          leftoverSnap.forEach((msgDoc) => {
            deleteBatch.delete(msgDoc.ref);
            deleteCount++;
            if (deleteCount === BATCH_LIMIT) {
              deleteCommits.push(deleteBatch.commit());
              deleteBatch = writeBatch(db);
              deleteCount = 0;
            }
          });

          if (deleteCount > 0) deleteCommits.push(deleteBatch.commit());
          await Promise.all(deleteCommits);

          await deleteDoc(convoRef); // fully remove
        }
      }
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    } finally {
      setConversationId(null);
      setShowDeleteModal(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop == 0) {
      setAutoScrollEnabled(false);
      loadOlderMessages();
    }
  };

  return (
    <>
      <Layout showHeader={false}>
        <div className="page-content h-100 chat-main">
          <div className="setting-content">
            <div className="d-flex align-items-center gap-2 pages-title-container">
              <img
                className="sidebar-toggle"
                src="/icons/menu.svg"
                alt="menu"
              />
              <h1 className="pages-header text-heading-h2 mb-0">Chats</h1>
            </div>
            <div className="user-chats-card users-chat-card-components">
              <div className="d-flex w-100">
                <div className="chat-friend-list-container flex-shrink-0">
                  <ChatFriendList
                    chatItems={conversations.map((item) => {
                      const userId = String(userProfile?.id);
                      const itemType = item.type;
                      const conversationKey = item.id || item.conversationId;

                      const otherParticipantId =
                        itemType === "direct"
                          ? item.participants.find(
                              (id) => normalizeUserId(id) !== userId,
                            )
                          : null;

                      // Fetch from users collection cache instead of participantDetails
                      const otherUser = otherParticipantId
                        ? userDetailsCache[otherParticipantId]
                        : null;

                      const isUserLastMessage = isLastMessageFromUser(
                        item.lastMessageSenderId,
                        userId,
                      );

                      // Get sender name from users collection cache
                      const otherSenderName =
                        !isUserLastMessage && item.lastMessageSenderId
                          ? userDetailsCache[item.lastMessageSenderId]?.name ||
                            item.participantDetails?.[item.lastMessageSenderId]
                              ?.name ||
                            null
                          : null;

                      const unreadCount = getUnreadCountForUser(
                        item.unreadCount,
                        userId,
                      );

                      return {
                        conversationId: conversationKey,
                        name:
                          itemType === "group"
                            ? item.metadata?.groupName
                            : otherUser?.name ||
                              otherUser?.full_name ||
                              "Unknown",
                        profileImage:
                          itemType === "group"
                            ? null
                            : otherUser?.profileImage ||
                              otherUser?.image ||
                              null,
                        otherParticipantId: otherParticipantId,
                        lastMessage: item.lastMessage,
                        isUserLastMessage: isUserLastMessage,
                        lastMessageTime: item.lastMessageTime,
                        otherSenderName: otherSenderName,
                        unreadCount,
                        isUnread: isConversationUnreadForUser(
                          item.unreadCount,
                          userId,
                        ),
                        type: itemType,
                        isGroup: itemType === "group",
                        members:
                          itemType === "group"
                            ? item.participants.length
                            : undefined,
                        isOnline:
                          itemType === "direct" && otherParticipantId != null
                            ? onlineStatusMap[
                                normalizeUserId(otherParticipantId)
                              ]
                            : undefined,
                        participants: item.participants,
                      };
                    })}
                    onSelectUser={(
                      conversationId,
                      type,
                      profileImage,
                      name,
                      members,
                    ) => {
                      handleSelectUser(
                        conversationId,
                        type,
                        profileImage,
                        name,
                        members,
                      );
                    }}
                    onDeleteConversation={handleDelete}
                    activeConversation={conversationId}
                    handleAddClick={showAddNewMessage}
                    showUnreadConversations={() => setShowUnread(!showUnread)}
                    showUnread={showUnread}
                  />
                </div>

                <div
                  className="flex-grow-1 flex-column chats-contents active-chat"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  {showAddMessage ? (
                    <AddNewMessage
                      setShowAddMessage={handleAddNewMessageClose}
                      onChatCreated={handleSelectUser}
                    />
                  ) : conversationId ? (
                    <>
                      <div
                        className="d-flex justify-content-between chats-card-content align-items-center"
                        style={{
                          flexShrink: 0,
                          padding: "12px 16px",
                          background: "#1a1f29",
                          zIndex: 10,
                        }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className="back-arrow-for-chats">
                            <img
                              className="chats-back-arrow"
                              src="/icons/back-arrow.svg"
                              alt="back"
                              onClick={() => setConversationId(null)}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                          <div className="chat-profile-image-user">
                            <img
                              className="chat-profile-self-img"
                              src={
                                currentChatType === "group"
                                  ? "/icons/group-user.svg"
                                  : clickedUserInfo.profileImage
                              }
                              alt="profile"
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                              }}
                            />
                            <span
                              className="text-heading-h3"
                              style={{ color: "#d0d8df" }}
                            >
                              {clickedUserInfo.name}
                              {currentChatType === "group" && (
                                <span
                                  className="text-body-b2"
                                  style={{
                                    marginLeft: "8px",
                                    color: "#b3ddff",
                                  }}
                                >
                                  ({clickedUserInfo.members} members)
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div
                        className="chat-container d-flex flex-column flex-grow-1"
                        style={{
                          overflowY: "auto",
                          flexGrow: 1, // fill remaining space below header
                          minHeight: 0, // important for flexbox scroll to work correctly
                          padding: "12px 16px",
                          // background: "#12161f", // optional, for contrast
                        }}
                        // ref={scrollContainerRef}
                      >
                        <div
                          ref={scrollContainerRef}
                          className="no-scrollbar mt-auto d-flex flex-column flex-grow-1"
                          style={{ overflowY: "auto", height: "90%" }}
                          onScroll={handleScroll}
                        >
                          <div className="message-group">
                            {[...messages].reverse().map((msg) => {
                              // Get fresh user data from users collection cache
                              const senderUserData = msg.senderId
                                ? userDetailsCache[String(msg.senderId)]
                                : null;

                              // Use fresh data from users collection, fallback to message data
                              const senderName =
                                senderUserData?.name ||
                                senderUserData?.full_name ||
                                msg.senderName ||
                                "Unknown";
                              const senderImage =
                                senderUserData?.profileImage ||
                                senderUserData?.image ||
                                msg.senderProfileImage ||
                                "/icons/james.svg";

                              if (msg.senderId == userProfile?.id) {
                                return (
                                  <React.Fragment key={`sent-${msg.id}`}>
                                    <ReceiveChat
                                      key={`sent-${msg.id}`}
                                      response={msg.content}
                                      replyCount={msg.replyTo?.count || 0}
                                      responder={
                                        userProfile?.id == msg.senderId
                                          ? "You"
                                          : senderName
                                      }
                                      time={formatTime(msg.timestamp)}
                                      userImage={
                                        userProfile?.image || "/icons/james.svg"
                                      }
                                      userId={msg.senderId}
                                      isFile={msg.isFile}
                                      fileInfo={msg?.file}
                                      fileType={msg?.file?.type}
                                      isUploading={msg.isUploading}
                                      uploadProgress={msg.uploadProgress}
                                      onDownload={() =>
                                        handleFileClick(msg?.file)
                                      }
                                      conversationId={conversationId}
                                      messageId={msg.id}
                                      isEditing={msg.isEditing}
                                      disableEdit={msg.disableEdit}
                                      onLike={handleLike}
                                      reactionSummary={Object.fromEntries(
                                        Object.entries(
                                          msg.reactions || {},
                                        ).filter(
                                          ([key]) =>
                                            key?.toLowerCase?.() !== "like",
                                        ),
                                      )}
                                      // userReaction={
                                      //   Object.entries(msg.reactions || {}).find(([emoji, users]) =>
                                      //     users.includes(String(userProfile?.id))
                                      //   )?.[0] || null
                                      // }
                                      // isLiked={msg.reactions?.like?.includes(String(userProfile?.id)) || false}
                                      // isLiked={Array.isArray(msg.reactions?.like)
                                      //   ? msg.reactions.like.length > 0
                                      //   : false}
                                      onEdit={handleEdit}
                                      onReply={handleReply}
                                      onSaveEdit={handleSaveEdit}
                                      onCancelEdit={handleCancelEdit}
                                      onDelete={handleDeleteMessage}
                                      replyMessage={msg.replyTo}
                                      journal={msg?.journal ?? {}}
                                    />
                                  </React.Fragment>
                                );
                              } else {
                                return (
                                  <SendChat
                                    key={`received-${msg.id}`}
                                    username={senderName}
                                    title={msg.content}
                                    time={formatTime(msg.timestamp)}
                                    // botImage="/icons/james.svg"
                                    userImage={senderImage}
                                    senderId={msg.senderId}
                                    isFile={msg.isFile}
                                    fileInfo={msg?.file}
                                    fileType={msg?.file?.type}
                                    onDownload={() =>
                                      handleFileClick(msg?.file)
                                    }
                                    messageId={msg.id}
                                    onLike={handleLike}
                                    reactionSummary={Object.fromEntries(
                                      Object.entries(
                                        msg.reactions || {},
                                      ).filter(
                                        ([key]) =>
                                          key?.toLowerCase?.() !== "like",
                                      ),
                                    )}
                                    // isLiked={msg.reactions?.like?.includes(String(userProfile?.id)) || false}
                                    isLiked={
                                      Array.isArray(msg.reactions?.like)
                                        ? msg.reactions.like.length > 0
                                        : false
                                    }
                                    onReply={handleReply}
                                    conversationId={conversationId}
                                    replyCount={msg.replyTo?.count || 0}
                                    replyMessage={msg.replyTo}
                                    journal={msg?.journal ?? {}}
                                  />
                                );
                              }
                            })}
                          </div>
                        </div>

                        {!messages?.some((msg) => msg.isEditing) &&
                        !replyingTo ? (
                          <ChatMessageBox
                            conversationId={conversationId}
                            userProfile={userProfile}
                            onSendClick={handleSendMessage}
                            disabled={!isClientReady}
                            disabledButton={disabledEditorButton}
                            participants={
                              conversations.find((c) => c.id === conversationId)
                                ?.participantDetails
                            }
                          />
                        ) : !isMember ? (
                          <div
                            className="p-3 mb-2 text-center text-white"
                            style={{
                              backgroundColor: "#EB5757",
                              borderRadius: "8px",
                            }}
                          >
                            You can't send messages to this group because you're
                            no longer a member.
                          </div>
                        ) : !messages?.some((msg) => msg.isEditing) &&
                          replyingTo ? (
                          // Reply mode message box
                          <ChatMessageBox
                            conversationId={conversationId}
                            userProfile={userProfile}
                            onSendClick={handleSendMessage}
                            disabled={!isClientReady}
                            replyingTo={replyingTo}
                            onCancelReply={handleCancelReply}
                            disabledButton={disabledEditorButton}
                            participants={
                              conversations.find((c) => c.id === conversationId)
                                ?.participantDetails
                            }
                          />
                        ) : (
                          // <></>
                          <ChatMessageBox
                            conversationId={conversationId}
                            userProfile={userProfile}
                            onSendClick={(newContent, file, mentionedIds) =>
                              handleSaveEdit(
                                messages.find((msg) => msg.isEditing)?.id,
                                newContent,
                                file,
                                mentionedIds,
                              )
                            }
                            initialText={
                              messages.find((msg) => msg.isEditing)?.content ||
                              ""
                            }
                            isEditing={true}
                            onCancel={() =>
                              handleCancelEdit(
                                messages.find((msg) => msg.isEditing)?.id,
                              )
                            }
                            disabled={!isClientReady}
                            showFileUpload={false}
                            disabledButton={disabledEditorButton}
                            participants={
                              conversations.find((c) => c.id === conversationId)
                                ?.participantDetails
                            }
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <div className="text-center">
                        <h3>Select a chat to start messaging</h3>
                        <p>
                          Choose a contact or group from the list to begin
                          conversation
                        </p>
                        {chatPartners.length === 0 && isClientReady && (
                          <p className="text-muted">
                            {connectionStatus === "connected"
                              ? "No conversations found. Start a new chat!"
                              : "Loading conversations..."}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {conversationId &&
                  currentChatType === "group" &&
                  !showAddMessage && (
                    <GroupAboutSidebar
                      conversationId={conversationId}
                      userProfile={userProfile}
                    />
                  )}
              </div>
            </div>
          </div>
        </div>
        {showDeleteModal && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
            aria-labelledby="deleteMessageModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div
                className="modal-content"
                style={{ backgroundColor: "#132534" }}
              >
                <div className="modal-header position-relative d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn-close btn-close-white position-absolute"
                    style={{
                      top: "0px",
                      right: "0px",
                      color: "#e6ecf1",
                    }}
                    onClick={handleCancelDelete}
                  ></button>
                  <h1
                    className="modal-title"
                    style={{ color: "#e6ecf1", fontSize: "24px" }}
                  >
                    Delete Conversation
                  </h1>
                </div>
                <div className="modal-body">
                  <p
                    className="text-body-b2 mb-4 text-center"
                    style={{ color: "#e6ecf1", fontSize: "18px" }}
                  >
                    Are you sure you want to delete this conversation?
                  </p>

                  <div
                    className="d-flex gap-3 justify-content-center"
                    style={{ marginTop: "32px" }}
                  >
                    <button
                      className="btn-1 btn-1-primary px-3 text-body-b1"
                      style={{ width: "182px", background: "#db4545" }}
                      onClick={handleConfirmDelete}
                    >
                      Yes
                    </button>
                    <button
                      className="btn-1 btn-1-basic px-3 text-body-b1"
                      style={{ width: "166px" }}
                      onClick={handleCancelDelete}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Layout>
      <FilePreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
        onDownload={handleDownload}
      />
    </>
  );
}
